/**
 * The lines of a just-in-time PO (BLU-1268, plan 90 section 5.4): one row per
 * blank, what the batch planned, what staff bought (editable), the unit price
 * in the PO currency (editable), the line total, and what happened to the
 * orders behind it (shipped / in progress / cancelled / removed from batch),
 * with "Leftover {n}" when more was bought than the orders still use.
 *
 * Mobile first, never a sideways scroll: below `md` each row is a stacked card
 * (name and outcome, then Planned / Bought / Unit price in three columns);
 * from `md` up the same DOM lays out as one table row (the field wrapper is
 * `md:contents`), under a visual header whose words each field also carries as
 * its own label for screen readers. Each input's name also names its row
 * ("Bought, Staple Tee · Black · M"): every row has the same two boxes, so the
 * label alone would not say which line a screen reader is in. Inputs are
 * `h-11` on a phone.
 *
 * Presentational: the page owns the draft and the save.
 */
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { cn } from '@/lib/utils';
import {
  type AddedJitLine,
  type JitDraft,
  type JitDraftSummary,
  type JitLineRow,
  jitRowName,
  leftoverUnits,
  rowPrice,
  rowPriceText,
  rowQty,
  rowQtyText,
} from '../lib/jit-lines';
import { JitAddBlankPicker } from './JitAddBlankPicker';
import { SupplierStockNote } from './SupplierStockNote';

const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 md:grid-cols-[minmax(0,1fr)_4.5rem_6rem_8rem_7rem_2.5rem] md:items-center';

export interface JitLinesCardProps {
  rows: JitLineRow[];
  draft: JitDraft;
  summary: JitDraftSummary;
  /** Open and the viewer may manage purchase orders. */
  editable: boolean;
  /** Still being bought. A completed or cancelled PO shows no live supplier stock: today's number says nothing about it. */
  open: boolean;
  /** The PO's supplier, as the org names it. */
  supplierName: string | null;
  /** The PO's currency (prices are entered and shown in it). */
  currency: string;
  locationId: string | null;
  outcomes: { loading: boolean; failed: boolean; retrying: boolean; retry: () => void };
  onQtyChange: (skuId: string, text: string) => void;
  onPriceChange: (skuId: string, text: string) => void;
  onRemove: (row: JitLineRow) => void;
  onAdd: (line: AddedJitLine) => void;
}

export function JitLinesCard({
  rows,
  draft,
  summary,
  editable,
  open,
  supplierName,
  currency,
  locationId,
  outcomes,
  onQtyChange,
  onPriceChange,
  onRemove,
  onAdd,
}: JitLinesCardProps) {
  const { t } = useTranslation('orders');
  const { format } = useCurrencyFormatter();
  const onPo = useMemo(() => new Set(rows.map((row) => row.skuId)), [rows]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('jit.lines.title')}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {editable ? t('jit.lines.helpEditable') : t('jit.lines.helpReadOnly')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {outcomes.failed && (
          <SectionError
            variant="compact"
            title={t('jit.lines.outcomesFailed')}
            body={t('jit.lines.outcomesFailedBody')}
            onRetry={outcomes.retry}
            retryLabel={t('jit.retry')}
            retrying={outcomes.retrying}
          />
        )}

        {rows.length === 0 ? (
          <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
            {t('jit.lines.empty')}
          </p>
        ) : (
          <div>
            <div
              aria-hidden="true"
              className={cn(
                ROW_GRID,
                'hidden border-b pb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground md:grid',
              )}
            >
              <span>{t('jit.lines.blank')}</span>
              <span className="text-right">{t('jit.lines.planned')}</span>
              <span className="text-right">{t('jit.lines.bought')}</span>
              <span className="text-right">{t('jit.lines.unitPrice', { currency })}</span>
              <span className="text-right">{t('jit.lines.lineTotal')}</span>
              <span />
            </div>
            <ul className="divide-y">
              {rows.map((row) => (
                <JitLineItem
                  key={row.skuId}
                  row={row}
                  draft={draft}
                  editable={editable}
                  open={open}
                  supplierName={supplierName}
                  currency={currency}
                  outcomesLoading={outcomes.loading}
                  formatMoney={(value) => format(value, currency)}
                  onQtyChange={onQtyChange}
                  onPriceChange={onPriceChange}
                  onRemove={onRemove}
                />
              ))}
            </ul>
          </div>
        )}

        {editable && <JitAddBlankPicker locationId={locationId} exclude={onPo} onAdd={onAdd} />}

        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t pt-3 text-sm">
          <span className="text-muted-foreground">{t('jit.lines.units', { count: summary.totalUnits })}</span>
          <span className="font-semibold tabular-nums">
            {t('jit.lines.total')} {format(summary.totalCost, currency)}
          </span>
        </div>
        {summary.missingPriceCount > 0 && (
          <NoteLine tone="warning" as="p">
            {t('jit.lines.missingPrice', { count: summary.missingPriceCount })}
          </NoteLine>
        )}
        {editable && summary.invalidCount > 0 && (
          <NoteLine tone="warning" as="p">
            {t('jit.lines.invalid', { count: summary.invalidCount })}
          </NoteLine>
        )}
      </CardContent>
    </Card>
  );
}

interface JitLineItemProps {
  row: JitLineRow;
  draft: JitDraft;
  editable: boolean;
  open: boolean;
  supplierName: string | null;
  currency: string;
  outcomesLoading: boolean;
  formatMoney: (value: number) => string;
  onQtyChange: (skuId: string, text: string) => void;
  onPriceChange: (skuId: string, text: string) => void;
  onRemove: (row: JitLineRow) => void;
}

function JitLineItem({
  row,
  draft,
  editable,
  open,
  supplierName,
  currency,
  outcomesLoading,
  formatMoney,
  onQtyChange,
  onPriceChange,
  onRemove,
}: JitLineItemProps) {
  const { t } = useTranslation('orders');
  const name = jitRowName(row);
  const qty = rowQty(row, draft);
  const qtyInvalid = qty === null;
  const price = rowPrice(row, draft);
  const priceError =
    price.kind === 'invalid' ? t('jit.lines.priceInvalid') : price.kind === 'tooPrecise' ? t('jit.lines.priceTooPrecise') : null;
  const units = qty ?? 0;
  const lineTotal = units > 0 && price.kind === 'ok' && price.value !== null ? price.value * units : null;
  const leftover = leftoverUnits(row, units);
  const notBought = units === 0 && !qtyInvalid;
  const ids = `jit-line-${row.skuId}`;

  return (
    <li className={cn(ROW_GRID, 'py-3', notBought && !row.added && 'text-muted-foreground')}>
      {/* Blank, then what happened to the orders behind it. */}
      <div className="min-w-0">
        <p className={cn('text-sm font-medium', notBought ? 'text-muted-foreground' : 'text-foreground')}>
          {name}
        </p>
        {row.sku && <p className="truncate font-mono text-[11px] text-muted-foreground">{row.sku}</p>}
        {open && (
          <SupplierStockNote
            stock={row.supplierStock}
            eta={row.nextDeliveryEta}
            supplierName={supplierName}
          />
        )}
        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
          {row.added ? (
            <span className="text-primary-strong">{t('jit.outcome.added')}</span>
          ) : outcomesLoading && !row.outcome ? (
            <Skeleton className="h-3 w-28" />
          ) : (
            <OutcomeLine row={row} />
          )}
          {notBought && <Dot>{t('jit.outcome.notBought')}</Dot>}
          {leftover > 0 && (
            <Dot>
              <span className="font-medium text-amber-700">{t('jit.outcome.leftover', { count: leftover })}</span>
            </Dot>
          )}
        </div>
      </div>

      {/* Remove: top right on a phone, the last column from md up. */}
      <div className="col-start-2 row-start-1 flex justify-end md:col-start-6">
        {editable && (units > 0 || row.added) && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11 text-muted-foreground hover:text-foreground md:h-8 md:w-8"
            onClick={() => onRemove(row)}
            aria-label={t('jit.lines.remove', { name })}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>

      <div className="col-span-2 grid grid-cols-3 gap-2 md:contents">
        <Field label={t('jit.lines.planned')}>
          <span className="flex h-11 items-center text-sm tabular-nums md:h-9 md:justify-end">
            {row.planned === null ? <span className="text-muted-foreground">-</span> : row.planned}
          </span>
        </Field>

        <Field label={t('jit.lines.bought')} htmlFor={`${ids}-qty`} editable={editable}>
          {editable ? (
            <>
              <Input
                id={`${ids}-qty`}
                inputMode="numeric"
                autoComplete="off"
                value={rowQtyText(row, draft)}
                placeholder="0"
                aria-label={t('jit.lines.boughtAria', { name })}
                onChange={(event) => onQtyChange(row.skuId, event.target.value)}
                aria-invalid={qtyInvalid || undefined}
                aria-describedby={qtyInvalid ? `${ids}-qty-error` : undefined}
                className={cn('h-11 text-right tabular-nums md:h-9', qtyInvalid && 'border-destructive')}
              />
              {qtyInvalid && (
                <p id={`${ids}-qty-error`} className="mt-1 text-[11px] text-destructive">
                  {t('jit.lines.qtyInvalid')}
                </p>
              )}
            </>
          ) : (
            <span className="flex h-11 items-center text-sm tabular-nums md:h-9 md:justify-end">
              {row.saved === null ? <span className="text-muted-foreground">{t('jit.outcome.notBought')}</span> : row.saved}
            </span>
          )}
        </Field>

        <Field label={t('jit.lines.unitPrice', { currency })} htmlFor={`${ids}-price`} editable={editable}>
          {editable ? (
            <>
              <Input
                id={`${ids}-price`}
                inputMode="decimal"
                autoComplete="off"
                value={rowPriceText(row, draft)}
                placeholder="0.00"
                aria-label={t('jit.lines.unitPriceAria', { currency, name })}
                disabled={units === 0}
                onChange={(event) => onPriceChange(row.skuId, event.target.value)}
                aria-invalid={priceError ? true : undefined}
                aria-describedby={priceError ? `${ids}-price-error` : undefined}
                className={cn('h-11 text-right tabular-nums md:h-9', priceError && 'border-destructive')}
              />
              {priceError && (
                <p id={`${ids}-price-error`} className="mt-1 text-[11px] text-destructive">
                  {priceError}
                </p>
              )}
            </>
          ) : (
            <span className="flex h-11 items-center text-sm tabular-nums md:h-9 md:justify-end">
              {row.unitPrice === null ? <span className="text-muted-foreground">-</span> : formatMoney(row.unitPrice)}
            </span>
          )}
        </Field>
      </div>

      <div className="col-span-2 flex items-baseline justify-between gap-2 text-sm md:col-span-1 md:col-start-5 md:row-start-1 md:justify-end">
        <span className="text-xs text-muted-foreground md:sr-only">{t('jit.lines.lineTotal')}</span>
        <span className="tabular-nums font-medium">
          {lineTotal === null ? <span className="font-normal text-muted-foreground">-</span> : formatMoney(lineTotal)}
        </span>
      </div>
    </li>
  );
}

/** A labelled cell: the label shows on a phone and is read (not shown) from md up. */
function Field({
  label,
  htmlFor,
  editable = false,
  children,
}: {
  label: string;
  htmlFor?: string;
  editable?: boolean;
  children: ReactNode;
}) {
  const labelClass = 'mb-1 block text-[11px] text-muted-foreground md:sr-only';
  return (
    <div className="min-w-0">
      {editable && htmlFor ? (
        <label htmlFor={htmlFor} className={labelClass}>
          {label}
        </label>
      ) : (
        <span className={labelClass}>{label}</span>
      )}
      {children}
    </div>
  );
}

function Dot({ children }: { children: ReactNode }) {
  return (
    <>
      <span aria-hidden="true" className="text-muted-foreground/60">
        ·
      </span>
      <span>{children}</span>
    </>
  );
}

/** "2 shipped · 1 in progress · 1 cancelled", each in its state's colour; words carry the meaning. */
function OutcomeLine({ row }: { row: JitLineRow }) {
  const { t } = useTranslation('orders');
  const outcome = row.outcome;
  if (!outcome) return <span className="text-muted-foreground">-</span>;
  const parts: Array<{ key: string; text: string; className: string }> = [];
  if (outcome.shipped > 0)
    parts.push({ key: 'shipped', text: t('jit.outcome.shipped', { count: outcome.shipped }), className: 'text-emerald-700' });
  if (outcome.inProgress > 0)
    parts.push({ key: 'inProgress', text: t('jit.outcome.inProgress', { count: outcome.inProgress }), className: 'text-foreground' });
  if (outcome.cancelled > 0)
    parts.push({ key: 'cancelled', text: t('jit.outcome.cancelled', { count: outcome.cancelled }), className: 'text-amber-700' });
  if (outcome.removed > 0)
    parts.push({ key: 'removed', text: t('jit.outcome.removed', { count: outcome.removed }), className: 'text-amber-700' });
  if (parts.length === 0) {
    return <span className="text-muted-foreground">{t('jit.outcome.notPlanned')}</span>;
  }
  return (
    <>
      {parts.map((part, index) => (
        <span key={part.key} className="inline-flex items-center gap-1.5">
          {index > 0 && (
            <span aria-hidden="true" className="text-muted-foreground/60">
              ·
            </span>
          )}
          <span className={part.className}>{part.text}</span>
        </span>
      ))}
    </>
  );
}
